'use client';

import { Button, Modal, Popover } from '@common/components';
import { INTERACTIVE_SURFACE } from '@common/components/interaction';
import { useToast } from '@common/hooks';
import SvgChevrondown from '@common/icon/SvgChevrondown';
import SvgKebab from '@common/icon/SvgKebab';
import SvgPadlock from '@common/icon/SvgPadlock';
import { cn } from '@core/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import useEmblaCarousel from 'embla-carousel-react';
import React, { useCallback, useEffect, useState } from 'react';

import { AuthMutation } from '@/api';
import { Image } from '@/components/client';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { DONATION_ROUTES, STORAGE_KEY } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';

import { INQUIRY_TYPE } from '../constants';
import type { InquiryType } from '../types';

interface InquiryCardProps {
  inquiryId: number;
  className?: string;
  inquiryType?: string;
  contents?: string;
  createAt?: string;
  productName?: string;
  institutionName?: string;
  institutionLogoUrl?: string;
  answer?: string;
  writerYN?: boolean;
  optionItems?: string;
  privateYN?: boolean;
  title?: string;
  fileUrlList?: string[];
  mainImageUrl?: string;
  inquiryTypeCd?: InquiryType;
  isProductVisible?: boolean;
}

export const InquiryCard = ({
  inquiryId,
  className,
  inquiryType,
  writerYN,
  optionItems,
  privateYN,
  title,
  contents,
  createAt,
  productName,
  institutionName,
  institutionLogoUrl,
  answer,
  fileUrlList,
  mainImageUrl,
  inquiryTypeCd,
  isProductVisible = true,
}: InquiryCardProps) => {
  const router = useCustomRouter();
  const queryClient = useQueryClient();
  const { toast, toastError } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
  });

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- embla 캐러셀 API 준비 시 외부 상태와 초기 동기화
    onSelect();
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  //* 문의 삭제
  const { mutate: deleteInquiry, isPending: isDeletePending } = useMutation({
    ...AuthMutation['delete:/wello/v2/auth/home-inquiry/{homeInquiryId}'](),
    onSuccess: () => {
      toast({
        message: '문의가 삭제되었습니다.',
      });
      queryClient.invalidateQueries();
      setIsDeleteModalOpen(false);
    },
    onError: (error) => {
      toastError({
        error,
      });
    },
  });

  const handleEdit = () => {
    //* 문의 레이아웃 가드가 이 표시가 없으면 홈으로 되돌린다
    sessionStorage.setItem(
      STORAGE_KEY.IS_DONATION_INQUIRY_BY_GIFT_PRODUCT,
      'true',
    );

    router.push(
      DONATION_ROUTES.MY_INQUIRY_EDIT.withSearchParams({
        dynamicPath: {
          id: inquiryId,
        },
        searchParams: {
          redirect: window.location.pathname + window.location.search,
        },
      }),
    );
  };

  const handleDelete = () => {
    deleteInquiry({
      path: {
        homeInquiryId: inquiryId,
      },
    });
  };

  const isDisabledMore = privateYN && !writerYN;
  const moreButtonText = answer ? '답변완료' : '답변대기';
  const isShowProductArea =
    isProductVisible && inquiryTypeCd !== INQUIRY_TYPE.ETC;

  return (
    <article className={cn('flex flex-col', className)}>
      <div className="relative">
        <button
          className={cn(
            'flex w-full flex-col gap-8 p-20 text-left',
            !isDisabledMore && INTERACTIVE_SURFACE,
          )}
          disabled={isDisabledMore}
          aria-expanded={isOpen}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <span className="size-fit rounded-xsmall bg-B-50 px-6 py-2 font-600 text-[#3667c0] typo-caption1">
            {inquiryType ?? '문의'}
          </span>
          <span className="flex items-center gap-4">
            {privateYN && (
              <SvgPadlock className="size-12 shrink-0 text-Gray-700" />
            )}
            <span className="font-500 text-Gray-900 typo-body3 mobile-tablet:text-Gray-black">
              {title}
            </span>
          </span>
          <span className="flex w-full items-center justify-between">
            <span className="font-400 text-Gray-550 typo-caption1">
              {dayjs(createAt).format('YYYY.MM.DD')}
            </span>
            <span className="flex items-center gap-4 font-600 text-Gray-700 typo-caption1">
              {moreButtonText}
              <SvgChevrondown
                className={cn('size-12 text-Gray-550 duration-150', {
                  'rotate-180': isOpen,
                })}
              />
            </span>
          </span>
        </button>

        {writerYN && (
          <Popover.Root floatingPadding={8} placement="bottom-end">
            <Popover.Trigger
              className={cn(
                'absolute right-12 top-16 w-fit rounded-pill p-8',
                INTERACTIVE_SURFACE,
              )}
              aria-label="문의 더보기"
            >
              <SvgKebab className="size-16 text-Gray-700" />
            </Popover.Trigger>
            <Popover.Content className="flex flex-col px-0 py-12 font-400 text-Gray-900 typo-body3">
              <button
                type="button"
                className="w-118 px-16 py-8 text-left hover:bg-Gray-25"
                onMouseDown={handleEdit}
              >
                수정
              </button>
              <button
                type="button"
                className="w-118 px-16 py-8 text-left hover:bg-Gray-25"
                onMouseDown={() => setIsDeleteModalOpen(true)}
              >
                삭제
              </button>
            </Popover.Content>
          </Popover.Root>
        )}
      </div>
      <div
        className={cn(
          'flex flex-col overflow-hidden overflow-y-auto border-t border-Gray-200 bg-Gray-25 px-20 duration-300 ease-in-out hide-scrollbar',
          {
            'max-h-0 opacity-0 py-0': !isOpen,
            'max-h-[1000px] opacity-100 py-20': isOpen,
          },
        )}
        style={{
          transitionProperty: 'max-height, opacity, padding',
        }}
      >
        <div
          className={cn('flex flex-col gap-12', {
            'border-b border-Gray-200 pb-16': answer,
          })}
        >
          {isShowProductArea && (
            <div className="flex gap-12">
              <div className="relative size-72 overflow-hidden rounded-small border border-Gray-black/5">
                <Image
                  src={mainImageUrl}
                  alt="답례품 썸네일 이미지"
                  width={144}
                  height={144}
                  className="size-full object-cover object-center"
                />
              </div>
              <div className="flex flex-1 flex-col gap-2">
                <div className="flex items-center gap-2">
                  <div className="relative size-20 overflow-hidden rounded-pill border border-Gray-300">
                    <Image
                      src={institutionLogoUrl}
                      width={40}
                      height={40}
                      alt="지자체 로고 이미지"
                      className="size-full object-cover object-center"
                    />
                  </div>
                  <p className="line-clamp-1 flex-1 font-600 text-Gray-600 typo-caption1">
                    {institutionName}
                  </p>
                </div>
                <p className="line-clamp-1 font-500 text-Gray-900 typo-body4">
                  {productName}
                </p>
                {optionItems && (
                  <p className="line-clamp-1 flex-1 pt-4 font-400 text-Gray-600 typo-caption1">
                    {`옵션: ${optionItems}`}
                  </p>
                )}
              </div>
            </div>
          )}
          <p className="whitespace-pre-line font-400 text-Gray-700 typo-body4">
            {contents}
          </p>
          {fileUrlList && fileUrlList.length > 0 && (
            <ul className="flex items-center gap-8 overflow-x-auto hide-scrollbar">
              {fileUrlList?.map((url) => (
                <li key={url}>
                  <button
                    type="button"
                    className="relative size-72 overflow-hidden rounded-small border border-Gray-black/5"
                    onClick={() => setIsPreviewOpen(true)}
                  >
                    <Image
                      src={url}
                      alt="문의 첨부 이미지"
                      width={144}
                      height={144}
                      className="size-full object-cover object-center"
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {answer && (
          <div className="flex flex-col gap-4 pt-20">
            <p className="font-600 tracking-[-0.28px] text-Gray-700 typo-body4">
              답변
            </p>
            <p className="whitespace-pre-line font-400 text-Gray-700 typo-body4">
              {answer}
            </p>
          </div>
        )}
      </div>
      <ResponsiveDialog
        modalHeader="문의 첨부 이미지"
        opened={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        className={{ modal: 'max-w-[800px]' }}
      >
        <div className="relative flex size-full flex-col">
          <div className="relative w-full overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {fileUrlList?.map((url) => (
                <div
                  key={url}
                  className="relative aspect-square min-w-0 flex-[0_0_100%]"
                >
                  <Image
                    src={url}
                    alt="답례품 문의 첨부 이미지"
                    width={850}
                    height={850}
                    className="size-full object-cover object-center"
                  />
                </div>
              ))}
            </div>
          </div>
          {fileUrlList && fileUrlList.length > 1 && (
            <div className="absolute bottom-0 right-0 mb-16 ml-auto mr-16 w-fit rounded-pill bg-Gray-black/20 px-8 py-2 font-600 text-Gray-white typo-caption2">
              {selectedIndex + 1} / {fileUrlList.length}
            </div>
          )}
        </div>
      </ResponsiveDialog>
      <Modal
        opened={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        className="relative mt-0 w-280 min-w-[unset] overflow-hidden !p-0 lg:mt-0 lg:min-w-[unset]"
      >
        <div className="flex flex-col items-center px-20 pb-24 pt-32">
          <p className="pb-8 font-700 text-Gray-900 typo-body2">확인</p>
          <p className="pb-24 font-400 text-Gray-700 typo-body3">
            문의를 삭제하시겠어요?
          </p>
          <div className="flex w-full items-center gap-8">
            <Button
              theme="sub-fill"
              dimensions="H48"
              onClick={() => setIsDeleteModalOpen(false)}
              className="flex-1"
              disabled={isDeletePending}
            >
              취소
            </Button>
            <Button
              dimensions="H48"
              onClick={handleDelete}
              className="flex-1"
              disabled={isDeletePending}
            >
              삭제
            </Button>
          </div>
        </div>
      </Modal>
    </article>
  );
};
